import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useFetchTriggerOptions from 'components/domain/automation/triggers/useFetchTriggerOptions';
import MultiSelectField, {
  MultiSelectFieldProps,
} from 'components/forms/multiSelectField/MultiSelectField';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import { RecipientType, TriggerActionType } from 'models/automation/Triggers';
import { orderBy } from 'utils/arrayUtils';
import { getRecipientOptionValue } from '../TriggersTab.utils';
import { MultiSelectOptionFormValue } from '../modal/TriggerAddEditModal.schema.utils';
import { isCopyAgencyRecipientDisabled } from '../modal/TriggerAddEditModal.utils';
import { formatRecipientOptionLabel } from './RecipientMultiSelectField.utils';

interface RecipientMultiSelectFieldProps
  extends Omit<MultiSelectFieldProps, 'fieldRenderer' | 'options'> {
  actionType?: TriggerActionType;

  /**
   * Blocks the selection of "Copy Agency" until there are other recipients selected.
   */
  requireSelectionForCopyAgency?: boolean;
}

const RecipientMultiSelectField = ({
  actionType,
  name,
  requireSelectionForCopyAgency,
  ...rest
}: RecipientMultiSelectFieldProps) => {
  const { t } = useTranslation();
  const { data, isLoading } = useFetchTriggerOptions();
  const currentSelectedRecipients = useWatch({ name }) as
    | MultiSelectOptionFormValue[]
    | undefined;

  const filteredRecipients = (data?.option.recipients ?? []).filter(
    (option) => {
      if (!actionType) {
        return true;
      }

      if (actionType === TriggerActionType.EMAIL) {
        return option.hasEmail;
      }

      if (
        actionType === TriggerActionType.SMS ||
        actionType === TriggerActionType.WHATSAPP
      ) {
        return option.hasPhoneNumber;
      }

      return false;
    },
  );

  const recipientOptions = filteredRecipients.map((recipient) => {
    const isCopyAgencyAndDisabled =
      requireSelectionForCopyAgency &&
      recipient.type === RecipientType.COPY_AGENCY &&
      isCopyAgencyRecipientDisabled(currentSelectedRecipients);

    return {
      disabled: isCopyAgencyAndDisabled,
      label: recipient.name,
      value: getRecipientOptionValue({
        recipientUid: recipient.uid,
        type: recipient.type,
      }),
      data: recipient,
    };
  });

  const options = orderBy(recipientOptions, [{ field: 'label', order: 'asc' }]);

  if (isLoading) {
    return <LoadingPlaceholder />;
  }

  return (
    <MultiSelectField
      colSmInput={8}
      colSmLabel={4}
      formatOptionLabel={(option) => formatRecipientOptionLabel(option, t)}
      isSelectAllEnabled
      name={name}
      options={options}
      {...rest}
    />
  );
};

export default RecipientMultiSelectField;
